import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import { loadConversation } from "@redux/Hermes/hermes.action";
import { toTitleCase } from "@containers/Hermes/utils/companyLinks";
import type { AppDispatch, ContactsOfferBlock, ContactsOfferCompany } from "@containers/Hermes/types";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import ActionCardShell, { SelectedCompaniesChip, resultsHeading } from "./ActionCardShell";

/**
 * The contacts card, opened from a companies action bar's "Request contacts".
 * ChatShell renders it in place of the composer while it is open.
 *
 *   card (companies + who to look for) ──▶ POST hermes/contacts/fetch
 *     └─ "N companies ⌄" ──▶ company picker (up to 50) ──▶ back to card
 *
 * There is no pre-spend cost step: credits are charged per contact actually
 * found (email/phone/both), which is only known once the batch enrichment call
 * returns — so "Request contacts" is itself the spend. Results are attached to
 * the message (a contacts_result block) or emailed, so a success reloads the
 * conversation to show them.
 */

type Opt = { label: string; value: string };

// Same values as workspace's EnrichmentInputsModal (CONTACT_TYPE_OPTS) —
// hermes/contacts/fetch's contact_type accepts "email" | "phone" | "both".
const CONTACT_TYPE_OPTIONS: Opt[] = [
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "Email & Phone", value: "both" },
];

const MAX_MULTI_SELECT = 5;
const MAX_CONTACTS_PER_COMPANY = 5;
// Per-request cap enforced by POST enrichment/companies/.
const DEFAULT_MAX_COMPANIES = 50;

// Same source workspace enrichment uses (EnrichmentInputsModal) — department
// and country values POST enrichment/companies/ doesn't recognize are
// rejected with a 400, so the picker must offer only these.
const contactDropdownUrl = getApiUrl("CompanyContactDetails/contactApolloDropdown");

type ContactsCardProps = {
  block: ContactsOfferBlock;
  conversationId: string;
  messageId: number;
  onClose: () => void;
  modal?: boolean;
  // Records the answer's result holds, for the heading when opened from the bar beneath the table.
  totalRecords?: number;
  // Opens the full table to choose from every record (given only when the result is bigger than the preview).
  onCustomise?: () => void;
};

/** One entry per company: the offer also carries a match-only alias entry (same id, alternate name). */
function uniqueCompanies(offered: ContactsOfferCompany[]): ContactsOfferCompany[] {
  const seen = new Set<string>();
  return offered.filter((company) => {
    const key = company.company_id || company.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function ContactsCard({ block, conversationId, messageId, onClose, modal, onCustomise, totalRecords }: ContactsCardProps) {
  const analytics = useHermesAnalytics();
  const dispatch = useDispatch<AppDispatch>();
  const maxCompanies = block.max_rows || DEFAULT_MAX_COMPANIES;

  const companies = useMemo(() => uniqueCompanies(block.companies ?? []), [block.companies]);
  const pickerCompanies = useMemo(() => companies.map((company) => ({ key: company.company_id || company.name, name: toTitleCase(company.display || company.name) })), [companies]);

  // Every company selected, up to what one request can carry.
  const selectedKeys = useMemo(() => new Set(pickerCompanies.slice(0, maxCompanies).map((company) => company.key)), [pickerCompanies, maxCompanies]);
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState<Opt[]>([]);
  const [country, setCountry] = useState<Opt[]>([]);
  const [contactType, setContactType] = useState<Opt>(CONTACT_TYPE_OPTIONS[2]);
  const [requestedContacts, setRequestedContacts] = useState(1);
  const [departmentOptions, setDepartmentOptions] = useState<Opt[]>([]);
  const [countryOptions, setCountryOptions] = useState<Opt[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  // At least one of job title/department/country is required — cleared on the
  // next edit to any of the three, same pattern as EnrichmentInputsModal.
  const [hasFieldsError, setHasFieldsError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(contactDropdownUrl)
      .then(({ data }) => {
        const message = data?.message || {};
        setDepartmentOptions(message.department || []);
        setCountryOptions((message.location || []).map((value: string) => ({ label: value, value })));
      })
      .catch((err) => {
        Sentry.captureException(err);
        toast.error("Failed to load department/country options");
      })
      .finally(() => setLoadingOptions(false));
  }, []);

  const selected = companies.filter((company) => selectedKeys.has(company.company_id || company.name));
  const selectedCount = selected.length;

  const limitedChange = (setter: (next: Opt[]) => void, noun: string) => (value: readonly Opt[] | null) => {
    const next = value ? [...value] : [];
    if (next.length > MAX_MULTI_SELECT) {
      toast.error(`You can select up to ${MAX_MULTI_SELECT} ${noun}.`);
      return;
    }
    setter(next);
    setHasFieldsError(false);
  };

  const handleDiscard = () => {
    if (busy) return;
    onClose();
  };

  const handleRequest = async () => {
    if (busy || selectedCount === 0) return;
    if (!jobTitle.trim() && department.length === 0 && country.length === 0) {
      toast.error("Please fill at least one of Job title, Department, or Country.");
      setHasFieldsError(true);
      return;
    }
    setBusy(true);
    setError("");
    analytics.contactsRequested({ companyCount: selectedCount, requestedContacts, contactType: contactType.value });

    try {
      const { data } = await axios.post(getApiUrl("hermes/contacts/fetch"), {
        conversation_id: conversationId,
        message_id: messageId,
        anchor_table: block.anchor_table,
        // redirectionType (buyer/seller) is deliberately omitted — there's no
        // such toggle in this flow, and the tool defaults it to "importers".
        companies: selected.map((company) => ({
          company_id: company.company_id,
          company_name: company.name,
        })),
        count: requestedContacts,
        contact_type: contactType.value,
        contact_params: {
          job_title: jobTitle.trim() ? [jobTitle.trim()] : [],
          department: department.map((d) => d.value),
          seniority: [],
          location: country.map((c) => c.value),
        },
      });

      // The endpoint's own response carries the user-facing outcome sentence
      // (found N contacts, none found, etc.) — shown as-is rather than
      // reconstructed from fields this endpoint may not return.
      toast.success(data?.message || "Contacts request submitted. We'll email them to you shortly.");
      // Reload so the contacts this request attached to the message (or the
      // emailed-request marker) show.
      dispatch(loadConversation(conversationId));
      onClose();
    } catch (err: any) {
      // The backend returns a user-facing sentence for every expected refusal
      // (insufficient credits, batch too large, invalid department/location,
      // count over the per-company cap of 5) — the tool raises {"error": "..."},
      // which the view maps to an HTTP 400, so check both response shapes.
      const detail = err?.response?.data?.message || err?.response?.data?.error;
      if (!detail) Sentry.captureException(err);
      const failure = detail || "Contacts could not be fetched. Please try again.";
      setError(failure);
      toast.error(failure);
      setBusy(false);
    }
  };

  return (
    <ActionCardShell modal={modal} title={modal ? "Request contacts" : resultsHeading(totalRecords ?? selectedCount)} primaryLabel="Request contacts" busyLabel="Requesting…" busy={busy} primaryDisabled={selectedCount === 0} onPrimary={handleRequest} onDiscard={handleDiscard}>
      {/* A selection the user ticked in the table is not repeated back to them. */}
      {modal ? null : <SelectedCompaniesChip companies={pickerCompanies} selectedKeys={selectedKeys} onCustomise={onCustomise} />}

      <div className="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
        <div className="tw-min-w-0">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">Contacts per company *</label>
          <div className="tw-flex tw-items-center tw-gap-2 tw-h-9">
            <button type="button" onClick={() => setRequestedContacts((n) => Math.max(1, n - 1))} disabled={busy || requestedContacts <= 1} aria-label="Fewer contacts per company" className="tw-w-9 tw-h-9 tw-shrink-0 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
              −
            </button>
            <div className="tw-flex-1 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-200 tw-text-sm tw-text-gray-900">{requestedContacts}</div>
            <button type="button" onClick={() => setRequestedContacts((n) => Math.min(MAX_CONTACTS_PER_COMPANY, n + 1))} disabled={busy || requestedContacts >= MAX_CONTACTS_PER_COMPANY} aria-label="More contacts per company" className="tw-w-9 tw-h-9 tw-shrink-0 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
              +
            </button>
          </div>
        </div>
        <div className="tw-min-w-0">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">Job title</label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => {
              setJobTitle(e.target.value);
              setHasFieldsError(false);
            }}
            placeholder="eg. Director of procurement"
            disabled={busy}
            className={`tw-w-full tw-h-9 tw-rounded-lg tw-border tw-px-3 tw-text-sm tw-text-gray-900 tw-outline-none focus:tw-border-primary-purple-500 ${hasFieldsError ? "!tw-border-red-400" : "tw-border-gray-200"}`}
          />
        </div>
        <div className="tw-min-w-0">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">Department</label>
          <Select isMulti isDisabled={busy || loadingOptions} options={departmentOptions} value={department} onChange={limitedChange(setDepartment, "departments")} styles={hasFieldsError ? errorSelectStyles : selectStyles} placeholder="Select" menuPlacement="top" />
        </div>
        <div className="tw-min-w-0">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">Country</label>
          <Select isMulti isDisabled={busy || loadingOptions} options={countryOptions} value={country} onChange={limitedChange(setCountry, "countries")} styles={hasFieldsError ? errorSelectStyles : selectStyles} placeholder="Select" menuPlacement="top" />
        </div>
        <div className="tw-min-w-0">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">Contact type</label>
          <Select isDisabled={busy} options={CONTACT_TYPE_OPTIONS} value={contactType} onChange={(v) => setContactType(v || CONTACT_TYPE_OPTIONS[2])} styles={selectStyles} placeholder="Select" menuPlacement="top" />
        </div>
      </div>

      <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-gray-500">* Fill at least one of job title, department, or country.</p>
      {error ? (
        <p role="alert" className="tw-m-0 tw-mt-2 tw-text-xs tw-text-red-600">
          {error}
        </p>
      ) : null}
    </ActionCardShell>
  );
}

const selectStyles = {
  control: (b: object, s: { isFocused: boolean }) => ({
    ...b,
    minHeight: 36,
    borderRadius: "8px",
    fontSize: "14px",
    border: s.isFocused ? "1px solid #7143f1" : "1px solid #eaecf0",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(113,67,241,0.08)" : "none",
    background: "#fff",
  }),
  valueContainer: (b: object) => ({ ...b, padding: "0 12px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b: object) => ({ ...b, padding: "0 8px", color: "#667085" }),
  placeholder: (b: object) => ({ ...b, color: "#98A2B3", fontSize: "14px" }),
  singleValue: (b: object) => ({ ...b, color: "#101828", fontSize: "14px" }),
  menu: (b: object) => ({ ...b, zIndex: 1000, borderRadius: "8px", border: "1px solid #eaecf0", boxShadow: "0 8px 24px rgba(16,24,40,0.1)", fontSize: "14px" }),
  menuList: (b: object) => ({ ...b, maxHeight: 200 }),
  option: (b: object, { isSelected }: { isFocused: boolean; isSelected: boolean }) => ({
    ...b,
    backgroundColor: isSelected ? "rgba(111, 8, 241, 0.1)" : "transparent",
    color: "#1a1722",
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(111, 8, 241, 0.1)" },
  }),
};

const errorSelectStyles = {
  ...selectStyles,
  control: (b: object, s: { isFocused: boolean }) => ({
    ...(selectStyles.control(b, s) as object),
    border: s.isFocused ? "1px solid #7143f1" : "1px solid #f87171",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(113,67,241,0.08)" : "0 0 0 3px rgba(248,113,113,0.15)",
  }),
};
